import {
  FaBullseye,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaDoorOpen,
  FaFileAlt,
  FaHourglassHalf,
  FaUsers,
  FaUser,
  FaComments,
} from 'react-icons/fa';
import './MeetingBookingNotificationCard.css';

const DETAIL_ICONS = {
  'Meeting Purpose': FaBullseye,
  Room: FaDoorOpen,
  Date: FaCalendarAlt,
  Time: FaClock,
  Duration: FaHourglassHalf,
  'Booked By': FaUser,
  Team: FaUsers,
  'Expected Attendees': FaUser,
  Notes: FaFileAlt,
};

const buildDetailRows = (meta = {}) => {
  const bookedByDisplay =
    meta.bookedByDisplay ||
    (meta.bookedByEmail
      ? `${meta.bookedByName || 'Guest'} (${meta.bookedByEmail})`
      : meta.bookedByName || '');

  const attendeesValue =
    meta.attendeesCount != null
      ? String(meta.attendeesCount)
      : meta.attendeesLabel?.replace(/\s*Participant(s)?/i, '').trim() || '';

  return [
    { label: 'Meeting Purpose', value: meta.meetingPurpose },
    { label: 'Room', value: meta.roomName },
    { label: 'Date', value: meta.dateLabel },
    { label: 'Time', value: meta.timeLabel },
    { label: 'Duration', value: meta.durationLabel },
    { label: 'Booked By', value: bookedByDisplay },
    { label: 'Team', value: meta.teamName },
    { label: 'Expected Attendees', value: attendeesValue },
    { label: 'Notes', value: meta.notesText || meta.notes },
  ].filter((row) => Boolean(row.value));
};

const MeetingBookingNotificationCard = ({
  title,
  headline,
  statusLabel,
  teamName,
  companyName,
  metadata = {},
  createdAt,
  read = true,
  onClick,
  className = '',
}) => {
  const displayTitle = headline || title || metadata.headline || 'Meeting Room Booking';
  const displayStatus = statusLabel || metadata.statusLabel || 'Confirmed';
  const displayTeam = teamName || metadata.teamName || companyName || metadata.companyName || '';
  const footerMessage =
    metadata.footerMessage || 'Your meeting room booking has been successfully confirmed.';
  const detailRows = buildDetailRows(metadata);
  const participants = Array.isArray(metadata.participants) ? metadata.participants : [];

  return (
    <article
      className={`mbn-card ${read ? '' : 'mbn-card--unread'} ${className}`.trim()}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
    >
      <header className="mbn-header">
        <div className="mbn-header-main">
          <div className="mbn-mini-calendar" aria-hidden="true">
            <span className="mbn-mini-calendar-month">
              {metadata.startTime
                ? new Date(metadata.startTime).toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()
                : 'JUN'}
            </span>
            <span className="mbn-mini-calendar-day">
              {metadata.startTime ? new Date(metadata.startTime).getDate() : '17'}
            </span>
          </div>
          <div className="mbn-header-copy">
            <h3>{displayTitle}</h3>
            <div className="mbn-status-pill">
              <FaCheckCircle />
              <span>{displayStatus}</span>
            </div>
            {displayTeam ? <span className="mbn-team-label">{displayTeam}</span> : null}
          </div>
        </div>
        <div className="mbn-header-calendar" aria-hidden="true">
          <FaCalendarAlt />
          <span className="mbn-header-check">
            <FaCheckCircle />
          </span>
        </div>
      </header>

      <section className="mbn-details">
        {detailRows.map((row) => {
          const Icon = DETAIL_ICONS[row.label] || FaFileAlt;
          return (
            <div key={row.label} className="mbn-detail-row">
              <span className="mbn-detail-icon">
                <Icon />
              </span>
              <div className="mbn-detail-copy">
                <span>{row.label}</span>
                <strong>{row.value}</strong>
              </div>
            </div>
          );
        })}
      </section>

      {participants.length > 0 ? (
        <section className="mbn-participants">
          <div className="mbn-participants-head">
            <span className="mbn-participants-icon">
              <FaUsers />
            </span>
            <h4>Participants</h4>
          </div>
          <ul>
            {participants.map((participant, index) => (
              <li key={`${participant.email || participant.name || index}`}>
                {participant.name || 'N/A'}
                {participant.email ? ` (${participant.email})` : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <footer className="mbn-footer">
        <div className="mbn-footer-main">
          <span className="mbn-footer-check">
            <FaCheckCircle />
          </span>
          <div>
            <strong>Thank you!</strong>
            <p>{footerMessage}</p>
          </div>
        </div>
        <div className="mbn-footer-help">
          <p>Need to make changes? Please contact the administrator.</p>
          <span className="mbn-footer-chat" aria-hidden="true">
            <FaComments />
          </span>
        </div>
      </footer>

      {createdAt ? <span className="mbn-created-at">{createdAt}</span> : null}
    </article>
  );
};

export default MeetingBookingNotificationCard;
